import { StopList } from "@/types/campaign";
import { api } from "@/utils/api";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

export type StopListSettings = {
  excludeAlreadyDelivered: boolean;
};

export interface BaseUploadedFile {
  name: string;
  errors: string[];
}

export interface ValidatedUploadedFile extends BaseUploadedFile {
  valid: number;
  invalid: string[];
  duplicates: number;
}

export interface FailedUploadedFile extends BaseUploadedFile {
  errors: string[];
}

export type UploadedFile = ValidatedUploadedFile | FailedUploadedFile;

export interface FileUploadResponse {
  duplicates: number;
  files: UploadedFile[];
  valid: number;
}

export const stopListApi = {
  getLists: async () =>
    await api.get("apps/sales/stoplists").json<StopList[]>(),
  getById: (id: string) =>
    api.get(`apps/sales/stoplists/${id}`).json<StopList>(),
  searchByLassoid: (lassoid: string) =>
    api.get(`apps/sales/stoplists/${lassoid}`).json<StopList[]>(),
  delete: (id: string) => api.delete(`apps/sales/stoplists/${id}`),
  update: (
    id: string,
    data: { Name: string; Mandatory: boolean; Hidden: boolean },
  ) => api.put(`apps/sales/stoplists/${id}`, { json: data }).json<StopList>(),
  create: (formData: FormData, name: string, mandatory: boolean) =>
    api
      .post(
        `apps/sales/stoplists?Name=${encodeURIComponent(
          name,
        )}&Mandatory=${mandatory}`,
        { body: formData },
      )
      .json<StopList>(),
  verify: (formData: FormData) =>
    api
      .post("apps/exporter/exports/basicinfo/verify", { body: formData })
      .json<FileUploadResponse>(),

  getSettings: () => api.get("apps/sales/settings").json<StopListSettings>(),
  updateSettings: (data: StopListSettings) =>
    api.put("apps/sales/settings", { json: data }).json<StopListSettings>(),
};

export const stopListsQueryOptions = queryOptions({
  queryKey: ["stopLists"],
  queryFn: () => {
    return stopListApi.getLists();
  },
});

export const useStopLists = () => useQuery(stopListsQueryOptions);

export const useStopList = (id: string) =>
  useQuery({
    queryKey: ["stopList", id],
    queryFn: () => stopListApi.getById(id),
    enabled: id !== "virtual-all-leads",
  });

export const useSearchStopLists = (lassoid: string) =>
  useQuery({
    queryKey: ["stopLists", "search", lassoid],
    queryFn: () => stopListApi.searchByLassoid(lassoid),
    enabled: !!lassoid,
  });

export const useDeleteStopList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: stopListApi.delete,

    onMutate: async (deletedId: string) => {
      await queryClient.cancelQueries({ queryKey: ["stopLists"] });
      const previousStopLists = queryClient.getQueryData<StopList[]>([
        "stopLists",
      ]);
      if (previousStopLists) {
        const newStopLists = previousStopLists.filter((item) => {
          return String(item.id) !== String(deletedId);
        });

        queryClient.setQueryData(["stopLists"], newStopLists);
      }

      toast.success("Stoplisten er nu slettet");

      return { previousStopLists, deletedId };
    },

    onError: (err, deletedId, context) => {
      if (context?.previousStopLists) {
        queryClient.setQueryData(["stopLists"], context.previousStopLists);
      }
      toast.error("Der skete en fejl - stoplisten kunne ikke slettes.");
    },
    onSuccess: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["stopLists"] });
    },
  });
};

export const useCreateStopList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      formData,
      name,
      mandatory,
    }: {
      formData: FormData;
      name: string;
      mandatory: boolean;
    }) => stopListApi.create(formData, name, mandatory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stopLists"] });
      toast.success("Stoplisten blev oprettet");
    },
    onError: (error) => {
      if (
        !error?.message?.includes("format") &&
        !error?.message?.includes("CVR")
      ) {
        toast.error("Stoplisten kunne ikke oprettes, prøv igen");
      }
    },
  });
};

export const useUpdateStopList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { Name: string; Mandatory: boolean; Hidden: boolean };
    }) => stopListApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stopLists"] });
      toast.success("Stoplisten blev opdateret");
    },
    onError: () => {
      toast.error("Kunne ikke opdatere stoplisten, prøv igen");
    },
  });
};

export const useVerifyStopListFile = () => {
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return stopListApi.verify(formData);
    },
  });
};

export const useStopListSettings = () =>
  useQuery({
    queryKey: ["stopListSettings"],
    queryFn: stopListApi.getSettings,
    enabled: true,
  });

export const useUpdateStopListSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: stopListApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stopListSettings"] });
      toast.success("Stoplisten blev opdateret");
    },
  });
};
